import { useEffect, useState } from "react";
import { api } from "../lib/api";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A quiet, editorial badge that appears only when a new clip has been added
 * within the last 24h (mode "auto"), always (mode "on"), or never (mode "off").
 *
 * Visual: small caps, soft pulse, low opacity. No red, no urgency.
 * Default placement: anchored top-right of the Marquee strip.
 */
export const RecentlyUpdatedBadge = ({ mode = "auto" }) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (mode === "off") {
            setShow(false);
            return;
        }
        if (mode === "on") {
            setShow(true);
            return;
        }
        // auto — query latest clip timestamp
        let cancelled = false;
        api.get("/clips/latest")
            .then((r) => {
                if (cancelled) return;
                const ts = r.data?.latest_created_at;
                if (!ts) return setShow(false);
                const age = Date.now() - new Date(ts).getTime();
                setShow(age >= 0 && age <= DAY_MS);
            })
            .catch(() => setShow(false));
        return () => {
            cancelled = true;
        };
    }, [mode]);

    if (!show) return null;

    return (
        <div
            data-testid="recently-updated-badge"
            className="pointer-events-none absolute right-6 sm:right-10 lg:right-14 top-3 z-20 select-none"
            aria-label="Recently updated"
        >
            <span className="badge-soft inline-flex items-center gap-2 px-3 py-1.5 border border-border bg-background/70 backdrop-blur-md text-[10px] tracking-[0.28em] uppercase text-foreground/65">
                <span
                    aria-hidden
                    className="badge-dot inline-block w-1.5 h-1.5 rounded-full bg-foreground/70"
                />
                Recently Updated
            </span>
            <style>{`
                @keyframes badge-pulse-dot {
                    0%, 100% { opacity: 0.5; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.25); }
                }
                @keyframes badge-pulse-soft {
                    0%, 100% { opacity: 0.7; }
                    50% { opacity: 1; }
                }
                .badge-soft { animation: badge-pulse-soft 4.5s ease-in-out infinite; }
                .badge-dot { animation: badge-pulse-dot 2.6s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .badge-soft, .badge-dot { animation: none; }
                }
            `}</style>
        </div>
    );
};

export default RecentlyUpdatedBadge;
